import { useState, useMemo, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Columns2, ChevronDown } from 'lucide-react';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getVacancyColor(pct) {
  if (pct === 0) return 'text-emerald-600';
  if (pct < 25) return 'text-yellow-600';
  if (pct < 50) return 'text-orange-600';
  return 'text-red-600';
}

// Extract flat unit list with per-month vacancy data from by_building
function buildUnitData(byBuilding) {
  const units = [];
  if (!byBuilding?.length) return units;
  const firstMonth = byBuilding[0].months[0];
  if (!firstMonth) return units;

  // Collect unique units from month 0
  const seen = new Set();
  for (const b of byBuilding) {
    for (const u of b.months[0].units) {
      if (seen.has(u.unit_id)) continue;
      seen.add(u.unit_id);
      const monthlyData = {};
      // Collect this unit's data across all months from this building
      for (const m of b.months) {
        const unitInMonth = m.units.find(x => x.unit_id === u.unit_id);
        if (unitInMonth) {
          monthlyData[m.month] = {
            vacant_days: unitInMonth.vacant_days,
            total_days: unitInMonth.total_days,
            vacancy_pct: unitInMonth.vacancy_pct,
          };
        }
      }
      units.push({
        unit_id: u.unit_id,
        unit_number: u.unit_number,
        unit_size: u.unit_size || '?',
        property_name: b.property_name,
        property_id: b.property_id,
        label: `${b.property_name} - ${u.unit_number}`,
        months: monthlyData,
      });
    }
  }
  return units;
}

// Multi-select dropdown component
function MultiDropdown({ options, selected, onChange, label, renderOption, onSelectAll, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedCount = selected.length;
  const totalCount = options.length;
  const summary = selectedCount === 0 ? `All ${label}` : selectedCount === totalCount ? `All ${label}` : `${selectedCount} selected`;

  return (
    <div className="relative flex-1 min-w-0" ref={ref}>
      <button
        className="w-full flex items-center justify-between gap-1 px-2.5 py-1.5 rounded-md border bg-card text-xs hover:bg-muted/50 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`dropdown-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <span className="truncate">
          <span className="text-muted-foreground font-medium">{label}:</span> {summary}
        </span>
        <ChevronDown className={`h-3 w-3 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[180px] max-h-52 overflow-y-auto rounded-md border bg-card shadow-lg">
          {(onSelectAll || onClear) && (
            <div className="flex gap-1 px-2 py-1.5 border-b border-border/50">
              {onSelectAll && <button className="text-[10px] text-primary hover:underline" onClick={onSelectAll}>All</button>}
              {onClear && <button className="text-[10px] text-muted-foreground hover:underline ml-2" onClick={onClear}>Clear</button>}
            </div>
          )}
          {options.map(opt => {
            const val = typeof opt === 'object' ? opt.value : opt;
            const display = renderOption ? renderOption(opt) : String(val);
            const isSelected = selected.includes(val);
            return (
              <button
                key={val}
                className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-muted/50 transition-colors flex items-center gap-2 ${isSelected ? 'bg-primary/5 font-medium' : ''}`}
                onClick={() => onChange(isSelected ? selected.filter(s => s !== val) : [...selected, val])}
              >
                <span className={`h-3.5 w-3.5 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                  {isSelected && <span className="text-primary-foreground text-[9px]">&#10003;</span>}
                </span>
                <span className="truncate">{display}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Aggregate vacancy across selected units and months, optionally filtered by size
function aggregateVacancy(unitData, selectedUnits, selectedMonths, sizeFilter) {
  let totalVacant = 0;
  let totalDays = 0;
  let pool = unitData;
  if (sizeFilter && sizeFilter.length > 0) {
    pool = pool.filter(u => sizeFilter.includes(u.unit_size));
  }
  const filtered = selectedUnits.length > 0
    ? pool.filter(u => selectedUnits.includes(u.unit_id))
    : pool;

  for (const u of filtered) {
    for (const m of selectedMonths) {
      const md = u.months[m];
      if (md) {
        totalVacant += md.vacant_days;
        totalDays += md.total_days;
      }
    }
  }
  const pct = totalDays > 0 ? (totalVacant / totalDays * 100) : 0;
  return { totalVacant, totalDays, pct: Math.round(pct * 100) / 100 };
}

// Filter panel for one side
function FilterPanel({ unitData, units, months, unitSizes, onUnitsChange, onMonthsChange, onSizeFilter, sizeFilter, allUnitSizes }) {
  const filteredUnitOptions = unitData
    .filter(u => sizeFilter.length === 0 || sizeFilter.includes(u.unit_size))
    .map(u => ({ value: u.unit_id, label: u.label }));

  return (
    <div className="flex gap-2 items-start flex-1 min-w-0">
      <MultiDropdown
        label="Months"
        options={[1,2,3,4,5,6,7,8,9,10,11,12]}
        selected={months}
        onChange={onMonthsChange}
        renderOption={m => MONTH_NAMES[m]}
        onSelectAll={() => onMonthsChange([1,2,3,4,5,6,7,8,9,10,11,12])}
        onClear={() => onMonthsChange([])}
      />
      <MultiDropdown
        label="Size"
        options={allUnitSizes}
        selected={sizeFilter}
        onChange={onSizeFilter}
        onClear={() => onSizeFilter([])}
      />
      <MultiDropdown
        label="Units"
        options={filteredUnitOptions}
        selected={units}
        onChange={onUnitsChange}
        renderOption={opt => opt.label}
        onSelectAll={() => onUnitsChange(filteredUnitOptions.map(u => u.value))}
        onClear={() => onUnitsChange([])}
      />
    </div>
  );
}

// Results table for one side
function ResultsTable({ unitData, selectedUnits, selectedMonths, groupBy, sizeFilter, compact }) {
  let pool = unitData;
  if (sizeFilter && sizeFilter.length > 0) {
    pool = pool.filter(u => sizeFilter.includes(u.unit_size));
  }
  const filtered = selectedUnits.length > 0
    ? pool.filter(u => selectedUnits.includes(u.unit_id))
    : pool;

  if (selectedMonths.length === 0 || filtered.length === 0) {
    return <div className="py-6 text-center text-xs text-muted-foreground">Select months and units to see data</div>;
  }

  const agg = aggregateVacancy(unitData, selectedUnits, selectedMonths, sizeFilter);

  // Column widths: compact pulls Rate+Days closer to Unit
  const rateW = compact ? 'w-[80px]' : '';
  const daysW = compact ? 'w-[70px]' : '';

  if (groupBy === 'month') {
    return (
      <div className="space-y-2">
        <div className="px-3 py-2 bg-muted/30 rounded flex items-center justify-between">
          <span className="text-xs font-semibold">Overall</span>
          <span className={`text-sm font-bold tabular-nums ${getVacancyColor(agg.pct)}`}>{agg.pct}%</span>
          <span className="text-[10px] text-muted-foreground tabular-nums">{agg.totalVacant}/{agg.totalDays}d</span>
        </div>
        <Table>
          <TableHeader><TableRow className="bg-muted/20">
            <TableHead className="text-[10px] font-semibold uppercase py-1">Month</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase py-1 text-right">Rate</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase py-1 text-right">Days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {selectedMonths.sort((a,b) => a-b).map(m => {
              const mAgg = aggregateVacancy(unitData, selectedUnits, [m], sizeFilter);
              return (
                <TableRow key={m} className="hover:bg-muted/30">
                  <TableCell className="py-1 text-xs font-medium">{MONTH_NAMES[m]}</TableCell>
                  <TableCell className={`py-1 text-xs tabular-nums text-right font-semibold ${getVacancyColor(mAgg.pct)}`}>{mAgg.pct}%</TableCell>
                  <TableCell className="py-1 text-[10px] tabular-nums text-right text-muted-foreground">{mAgg.totalVacant}/{mAgg.totalDays}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (groupBy === 'unit') {
    return (
      <div className="space-y-2">
        <div className="px-3 py-2 bg-muted/30 rounded flex items-center justify-between">
          <span className="text-xs font-semibold">Overall</span>
          <span className={`text-sm font-bold tabular-nums ${getVacancyColor(agg.pct)}`}>{agg.pct}%</span>
          <span className="text-[10px] text-muted-foreground tabular-nums">{agg.totalVacant}/{agg.totalDays}d</span>
        </div>
        <Table>
          <TableHeader><TableRow className="bg-muted/20">
            <TableHead className="text-[10px] font-semibold uppercase py-1">Unit</TableHead>
            <TableHead className={`text-[10px] font-semibold uppercase py-1 text-right ${rateW}`}>Rate</TableHead>
            <TableHead className={`text-[10px] font-semibold uppercase py-1 text-right ${daysW}`}>Days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(u => {
              const uAgg = aggregateVacancy(unitData, [u.unit_id], selectedMonths, sizeFilter);
              return (
                <TableRow key={u.unit_id} className="hover:bg-muted/30">
                  <TableCell className="py-1 text-xs font-medium">{u.label}</TableCell>
                  <TableCell className={`py-1 text-xs tabular-nums text-right font-semibold ${rateW} ${getVacancyColor(uAgg.pct)}`}>{uAgg.pct}%</TableCell>
                  <TableCell className={`py-1 text-[10px] tabular-nums text-right text-muted-foreground ${daysW}`}>{uAgg.totalVacant}/{uAgg.totalDays}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  // groupBy === 'both' — units x months grid
  return (
    <div className="space-y-2">
      <div className="px-3 py-2 bg-muted/30 rounded flex items-center justify-between">
        <span className="text-xs font-semibold">Overall</span>
        <span className={`text-sm font-bold tabular-nums ${getVacancyColor(agg.pct)}`}>{agg.pct}%</span>
        <span className="text-[10px] text-muted-foreground tabular-nums">{agg.totalVacant}/{agg.totalDays}d</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-muted/20">
            <TableHead className="text-[10px] font-semibold uppercase py-1 sticky left-0 bg-muted/20">Unit</TableHead>
            {selectedMonths.sort((a,b) => a-b).map(m => (
              <TableHead key={m} className="text-[10px] font-semibold uppercase py-1 text-center min-w-[60px]">{MONTH_NAMES[m]}</TableHead>
            ))}
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(u => (
              <TableRow key={u.unit_id} className="hover:bg-muted/30">
                <TableCell className="py-1 text-[10px] font-medium whitespace-nowrap sticky left-0 bg-card">{u.label}</TableCell>
                {selectedMonths.sort((a,b) => a-b).map(m => {
                  const md = u.months[m];
                  const pct = md ? md.vacancy_pct : 0;
                  return (
                    <TableCell key={m} className={`py-1 text-[10px] tabular-nums text-center ${getVacancyColor(pct)}`}>
                      {pct}%
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function VacancyRateTab({ data }) {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const isCurrentYear = data?.year === currentYear;

  const unitData = useMemo(() => buildUnitData(data?.by_building), [data]);
  const allUnitSizes = useMemo(() => [...new Set(unitData.map(u => u.unit_size))].sort(), [unitData]);
  const allUnitIds = useMemo(() => unitData.map(u => u.unit_id), [unitData]);
  const displayMonths = useMemo(() => {
    if (isCurrentYear) return Array.from({ length: currentMonth }, (_, i) => i + 1);
    return [1,2,3,4,5,6,7,8,9,10,11,12];
  }, [currentMonth, isCurrentYear]);

  // Compare mode
  const [comparing, setComparing] = useState(false);

  // Left filters
  const [leftMonths, setLeftMonths] = useState([currentMonth]);
  const [leftUnits, setLeftUnits] = useState([]);
  const [leftSizeFilter, setLeftSizeFilter] = useState([]);

  // Right filters (comparison)
  const [rightMonths, setRightMonths] = useState([currentMonth]);
  const [rightUnits, setRightUnits] = useState([]);
  const [rightSizeFilter, setRightSizeFilter] = useState([]);

  // Default view: all units, current month
  const defaultAgg = aggregateVacancy(unitData, [], [currentMonth]);
  // YTD/full-year summary
  const summaryRows = displayMonths.map(m => {
    const a = aggregateVacancy(unitData, [], [m]);
    return { month: m, ...a };
  });
  const totalAgg = aggregateVacancy(unitData, [], displayMonths);

  return (
    <div className="space-y-4">
      {/* Default summary */}
      <div className="flex gap-3 max-w-2xl">
        <Card className="flex-1">
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-sm font-semibold">{MONTH_NAMES[currentMonth]} {data?.year} — All Units</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="flex items-baseline gap-3">
              <span className={`text-2xl font-bold tabular-nums ${getVacancyColor(defaultAgg.pct)}`}>{defaultAgg.pct}%</span>
              <span className="text-xs text-muted-foreground tabular-nums">{defaultAgg.totalVacant} vacant / {defaultAgg.totalDays} total days</span>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-1">
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-3">
              {isCurrentYear ? 'YTD' : ''} {data?.year}
              <span className={`text-base font-bold tabular-nums ${getVacancyColor(totalAgg.pct)}`}>{totalAgg.pct}%</span>
              <span className="text-[10px] text-muted-foreground tabular-nums font-normal">{totalAgg.totalVacant}/{totalAgg.totalDays}d</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="flex gap-2 flex-wrap">
              {summaryRows.map(r => (
                <div key={r.month} className="text-center min-w-[34px]">
                  <p className="text-[10px] text-muted-foreground">{MONTH_NAMES[r.month]}</p>
                  <p className={`text-xs font-semibold tabular-nums ${getVacancyColor(r.pct)}`}>{r.pct}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-2" />

      {/* Compare toggle + filters inline */}
      {comparing ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 pt-3 px-3">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="default" className="gap-1.5 text-xs flex-shrink-0" onClick={() => setComparing(false)} data-testid="compare-toggle">
                  <Columns2 className="h-3.5 w-3.5" /> Exit Compare
                </Button>
                <span className="text-xs font-semibold text-muted-foreground">Side A</span>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-3">
              <FilterPanel
                unitData={unitData} units={leftUnits} months={leftMonths}
                onUnitsChange={setLeftUnits} onMonthsChange={setLeftMonths}
                sizeFilter={leftSizeFilter} onSizeFilter={setLeftSizeFilter}
                allUnitSizes={allUnitSizes}
              />
              <ResultsTable unitData={unitData} selectedUnits={leftUnits} selectedMonths={leftMonths} groupBy="unit" sizeFilter={leftSizeFilter} />
            </CardContent>
          </Card>
          <Card className="overflow-hidden border-sky-200">
            <CardHeader className="pb-2 pt-3 px-3">
              <span className="text-xs font-semibold text-muted-foreground">Side B</span>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-3">
              <FilterPanel
                unitData={unitData} units={rightUnits} months={rightMonths}
                onUnitsChange={setRightUnits} onMonthsChange={setRightMonths}
                sizeFilter={rightSizeFilter} onSizeFilter={setRightSizeFilter}
                allUnitSizes={allUnitSizes}
              />
              <ResultsTable unitData={unitData} selectedUnits={rightUnits} selectedMonths={rightMonths} groupBy="unit" sizeFilter={rightSizeFilter} />
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="overflow-hidden max-w-2xl">
          <CardContent className="px-3 py-3 space-y-3">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs flex-shrink-0" onClick={() => setComparing(true)} data-testid="compare-toggle">
                <Columns2 className="h-3.5 w-3.5" /> Compare
              </Button>
              <FilterPanel
                unitData={unitData} units={leftUnits} months={leftMonths}
                onUnitsChange={setLeftUnits} onMonthsChange={setLeftMonths}
                sizeFilter={leftSizeFilter} onSizeFilter={setLeftSizeFilter}
                allUnitSizes={allUnitSizes}
              />
            </div>
            <div className="max-w-lg mx-auto">
              <ResultsTable unitData={unitData} selectedUnits={leftUnits} selectedMonths={leftMonths} groupBy="unit" sizeFilter={leftSizeFilter} compact />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
